import { useCallback, useState } from 'react';

import type { AuthFlow, AuthResponse, OriginName } from '~/interfaces/common';
import type { AccountProfileData } from '~/routes/auth/signup/-types/types';
import { createLoginHandler } from '~/services/login/factory';
import { createSignupHandler } from '~/services/signup/factory';
import { fetchJson } from '~/utils/fetch-json';
import { getLocalStorageItem } from '~/utils/local-storage';
import { redirectAfterAuth } from '~/utils/redirect-after-auth';
import { signAliasMessage } from './api';

export type AuthChallengeData = {
  alias?: string;
  sig?: `0x${string}`;
  // biome-ignore lint/suspicious/noExplicitAny: challenge params structure varies
  originalChallengeParams?: any;
  // biome-ignore lint/suspicious/noExplicitAny: assert data structure varies
  assert?: any;
};

interface AttestationChallengeProps {
  authFlow: AuthFlow;
}

export function useAttestationChallenge({
  authFlow,
}: AttestationChallengeProps) {
  const [isLoading, setIsLoading] = useState(true);

  const handleAuth = useCallback(
    async (originName: OriginName, challengeData: AuthChallengeData) => {
      try {
        let res!: AuthResponse | null;
        if (authFlow === 'login') {
          const handler = createLoginHandler(originName);
          res = await handler.handle({
            alias: challengeData.alias,
            sig: challengeData.sig,
            assert: challengeData.assert,
            originalChallengeParams: challengeData.originalChallengeParams,
          });
        }

        let usage: string | undefined;
        if (authFlow === 'signup') {
          const data = getLocalStorageItem<AccountProfileData>('signup');
          if (!data) {
            console.log('signup no data');
            return;
          }
          const handle = createSignupHandler(originName);
          res = await handle.handle({
            alias: challengeData.alias,
            sig: challengeData.sig,
            assert: challengeData.assert,
            data,
            originalChallengeParams: challengeData.originalChallengeParams,
          });
          usage = data.usage;
        }
        if (!res) {
          console.log('no res');
          return;
        }
        const token = res.result.access_token;

        if (usage) {
          await fetchJson('/identities/me', {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              metadata: {
                usage: usage,
              },
            }),
          });
        }

        redirectAfterAuth(token);
      } catch (error) {
        console.error('Authentication error:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const handleAttestationChallenge = useCallback(
    async (originName: OriginName, address: string) => {
      const { sig, originalChallengeParams } = await signAliasMessage(
        originName,
        address,
      );

      await handleAuth(originName, {
        alias: address,
        sig,
        originalChallengeParams,
      });
    },
    [],
  );

  return {
    isLoading,
    handleAttestationChallenge,
  };
}
